/*
  The metric cards of a case page's "results" block: which icons an editor can pick,
  and how the text after the number is stored and shown.

  Kept in one place because three things have to agree on it -- the admin editor that
  offers the icons, the save that accepts only known ones, and the page that draws them.
  Before, each had its own copy of a four-icon list.
*/

export const IMPACT_ICONS = [
  { value: 'trendingUp', label: 'Рост' },
  { value: 'users', label: 'Пользователи' },
  { value: 'zap', label: 'Скорость' },
  { value: 'clock', label: 'Время' },
  { value: 'calendar', label: 'Срок, даты' },
  { value: 'shield', label: 'Гарантия, надёжность' },
  { value: 'star', label: 'Рейтинг, качество' },
  { value: 'target', label: 'Цель, точность' },
  { value: 'award', label: 'Награда' },
  { value: 'wallet', label: 'Деньги, выручка' },
  { value: 'percent', label: 'Проценты, конверсия' },
  { value: 'heart', label: 'Лояльность' },
  { value: 'handshake', label: 'Клиенты, сделки' },
  { value: 'globe', label: 'Охват, география' },
  { value: 'layers', label: 'Масштаб, модели' },
  { value: 'package', label: 'Товары, каталог' },
  { value: 'ruler', label: 'Размеры, площадь' },
  { value: 'hammer', label: 'Производство' },
  { value: 'truck', label: 'Доставка' },
  { value: 'phone', label: 'Заявки, звонки' },
  { value: 'rocket', label: 'Запуск' },
  { value: 'check', label: 'Выполнено' },
] as const

export type ImpactMetricIcon = (typeof IMPACT_ICONS)[number]['value']

export const DEFAULT_IMPACT_ICON: ImpactMetricIcon = 'trendingUp'

const ICON_VALUES: ReadonlySet<string> = new Set(IMPACT_ICONS.map((i) => i.value))

/** A stored icon name, or the default for anything not in the list. */
export function normalizeImpactIcon(value: unknown): ImpactMetricIcon {
  return typeof value === 'string' && ICON_VALUES.has(value)
    ? (value as ImpactMetricIcon)
    : DEFAULT_IMPACT_ICON
}

/**
 * The text after a metric's number ("%", "+ лет", " дней").
 *
 * A space at the start is meaningful -- it is the gap between the number and the text --
 * so it is kept, as a single space; the end is trimmed. Trimming both sides made
 * "10 лет" impossible to write: the space was removed before it was saved.
 */
export function normalizeImpactSuffix(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmedEnd = value.replace(/\s+$/, '')
  if (trimmedEnd.trim().length === 0) return undefined
  return trimmedEnd.replace(/^\s+/, ' ')
}

/**
 * The text before a metric's number ("от ", "+", "~ "). The mirror of the suffix: the
 * space that matters is the one at the END, between the text and the number, so it is
 * kept (as a single space) and the start is trimmed.
 */
export function normalizeImpactPrefix(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmedStart = value.replace(/^\s+/, '')
  if (trimmedStart.trim().length === 0) return undefined
  return trimmedStart.replace(/\s+$/, ' ')
}

/** The prefix as drawn: the space before the number is non-breaking, like the suffix's. */
export function impactPrefixForDisplay(prefix: string | undefined): string | undefined {
  return prefix === undefined ? undefined : prefix.replace(/\s$/, '\u00A0')
}

/**
 * The suffix as drawn. The gap becomes a non-breaking space so that "10" and "+ лет"
 * cannot be split across two lines, and so that the space survives HTML whitespace rules.
 */
export function impactSuffixForDisplay(suffix: string | undefined): string | undefined {
  return suffix === undefined ? undefined : suffix.replace(/^\s/, ' ')
}

// A text metric ("Next.js", "AI") sits where a number would; long enough for a word or two,
// short enough not to break the card.
export const IMPACT_TEXT_MAX = 40

/**
 * A number typed by a person: "30", "3,4", "1 200". Blank or not a number gives
 * undefined -- NOT 0, which is what `Number('')` returns and what used to turn an empty
 * number field into a metric of 0 ("Next.js0").
 */
export function parseImpactNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value !== 'string') return undefined
  const compact = value.replace(/[\s\u00A0]+/g, '').replace(',', '.')
  if (compact === '') return undefined
  const n = Number(compact)
  return Number.isFinite(n) ? n : undefined
}

/** The text shown in place of a number, trimmed and capped; blank is none. */
export function normalizeImpactText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const text = value.trim().slice(0, IMPACT_TEXT_MAX)
  return text.length > 0 ? text : undefined
}

export type ImpactMetric = {
  label: string
  icon: ImpactMetricIcon
  value?: number
  text?: string
  prefix?: string
  suffix?: string
  precision?: number
}

/**
 * One metric as it is stored, from what the editor or a request sent. Null when it has no
 * label, or has neither a number nor a text. Text wins: a metric with text is drawn as that
 * text, so the number, prefix, suffix and decimals are not kept.
 *
 * The editor and the save both use this, so what the editor previews is what is stored.
 */
export function buildImpactMetric(raw: Record<string, unknown>): ImpactMetric | null {
  const label = typeof raw.label === 'string' ? raw.label.trim() : ''
  if (!label) return null

  const icon = normalizeImpactIcon(raw.icon)
  const text = normalizeImpactText(raw.text)
  if (text !== undefined) return { label, icon, text }

  const value = parseImpactNumber(raw.value)
  if (value === undefined) return null

  const decimals = parseImpactNumber(raw.precision)
  return {
    label,
    icon,
    value,
    prefix: normalizeImpactPrefix(raw.prefix),
    suffix: normalizeImpactSuffix(raw.suffix),
    precision: decimals === undefined ? undefined : Math.max(0, Math.trunc(decimals)),
  }
}
